import { getUpdatedConditionalFormatting } from '@thoughtspot/client-state-util';
import { CustomStylingConfig } from '@thoughtspot/custom-style-service';
import { create } from '@thoughtspot/logger';
import { isEnhancedCfEnabled, isGradientColoringEnabled, } from '@thoughtspot/session-service';
import { getId } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getEnvFlags } from '../env';
import { HEATMAP_GRADIENT_COLORS } from '../geomap/base/geo-constants';
import { BackgroundFormatTypes, ChartType, ConditionalFormattingComparisonTypes, Operators, } from '../types';
import { getColorScaleForSerie } from './chart-color-utils';
import { getAllConditionalFormattingRules, getChartType, getColumnPropertiesById, } from './chart-model-utils';
import { configOptions, isGeoMapChartType, isHighchartType, } from './chart-type-specification-service';
import { getMaxRangeValues, getMinRangeValue, } from './table-client-state-utils';
const logger = create('conditional-formatting-utils');
export const FontStyles = {
    underline: 'underline',
    strikeThrough: 'line-through',
    italic: 'italic',
    bold: 'bold',
    none: '',
    normal: 'normal',
};
export const VALUESLESS_OPERATORS = [
    Operators.IsNull,
    Operators.IsNotNull,
    Operators.IsEmpty,
    Operators.IsNotEmpty,
];
export const OPTIONSFORVALUE_OPERATORS = [Operators.Is, Operators.IsNot];
export const RANGE_OPERATORS = [Operators.IsBetween];
const NIL_DISALLOWED_OPERATORS = [
    Operators.LessThan,
    Operators.LessThanEqualTo,
    Operators.GreaterThan,
    Operators.GreaterThanEqualTo,
    Operators.Contains,
    Operators.DoesNotContain,
    Operators.StartsWith,
    Operators.EndsWith,
];
export const getMetricValues = (metric) => {
    if (VALUESLESS_OPERATORS.includes(metric.operator)) {
        return [];
    }
    if (RANGE_OPERATORS.includes(metric.operator)) {
        return [getMinRangeValue(metric), getMaxRangeValues(metric)];
    }
    return [metric.value];
};
export const isValidInputValue = (value) => {
    return !_.isNil(value) && value !== '';
};
export const isValidNumericInputValue = (value) => {
    return isValidInputValue(value) && !_.isNaN(Number(value));
};
export const showDecimalValue = (value) => {
    return (value.indexOf('.') === -1 || value.lastIndexOf('0') === value.length - 1);
};
export const OPERATOR_TO_TEXT = {
    [Operators.Is]: 'IS',
    [Operators.IsNot]: 'IS_NOT',
    [Operators.IsEmpty]: 'IS_EMPTY',
    [Operators.IsNotEmpty]: 'IS_NOT_EMPTY',
    [Operators.Contains]: 'CONTAINS',
    [Operators.DoesNotContain]: 'DOES_NOT_CONTAIN',
    [Operators.GreaterThan]: 'GREATER_THAN',
    [Operators.GreaterThanEqualTo]: 'GREATER_THAN_EQUAL_TO',
    [Operators.LessThan]: 'LESS_THAN',
    [Operators.LessThanEqualTo]: 'LESS_THAN_EQUAL_TO',
    [Operators.IsBetween]: 'BETWEEN',
    [Operators.StartsWith]: 'STARTS_WITH',
    [Operators.EndsWith]: 'ENDS_WITH',
    [Operators.EqualTo]: 'EQUAL',
    [Operators.NotEqualTo]: 'NOT_EQUAL',
    [Operators.IsNull]: 'IS_NULL',
    [Operators.IsNotNull]: 'IS_NOT_NULL',
};
export const getRowClass = (metric) => {
    const fontProperties = metric.fontProperties || {
        __typename: 'FontAttrs',
    };
    const classList = [];
    if (fontProperties.underline && fontProperties.strikeThrough) {
        classList.push('fontPropertyUnderlineStrikethrough');
    }
    else if (fontProperties.underline) {
        classList.push('fontPropertyUnderline');
    }
    else if (fontProperties.strikeThrough) {
        classList.push('fontPropertyStrikethrough');
    }
    return classList;
};
export const getStyles = (metric, shouldShowBackgroundColor = true) => {
    const fontProperties = metric.fontProperties || {
        __typename: 'FontAttrs',
    };
    let solidBackgroundAttrs = metric.solidBackgroundAttrs;
    if (!shouldShowBackgroundColor) {
        solidBackgroundAttrs = null;
    }
    return {
        textDecoration: `${fontProperties.underline ? FontStyles.underline : FontStyles.none} ${fontProperties.strikeThrough
            ? FontStyles.strikeThrough
            : FontStyles.none}`,
        fontStyle: fontProperties.italic
            ? FontStyles.italic
            : FontStyles.normal,
        fontWeight: fontProperties.bold ? FontStyles.bold : FontStyles.normal,
        backgroundColor: _.get(solidBackgroundAttrs, 'color'),
        color: fontProperties.color || '#000000',
    };
};
const doValueBasedComparison = (metric, lhsValue, rhsValue) => {
    if (RANGE_OPERATORS.includes(metric.operator) && _.isNil(lhsValue))
        return false;
    if (NIL_DISALLOWED_OPERATORS.includes(metric.operator) &&
        (_.isNil(lhsValue) || _.isNil(rhsValue)))
        return false;
    switch (metric.operator) {
        case Operators.Is:
        case Operators.EqualTo:
            return (_.isEqual(lhsValue, rhsValue) ||
                _.isEqual(_.toString(lhsValue), _.toString(rhsValue)));
        case Operators.IsNot:
        case Operators.NotEqualTo: {
            if (_.isNil(lhsValue) || _.isNil(rhsValue))
                return lhsValue !== rhsValue;
            return !_.isEqual(_.toString(lhsValue), _.toString(rhsValue));
        }
        case Operators.IsNull:
        case Operators.IsEmpty:
            return _.isNil(lhsValue);
        case Operators.IsNotNull:
        case Operators.IsNotEmpty:
            return !_.isNil(lhsValue);
        case Operators.LessThan:
            return _.toNumber(lhsValue) < _.toNumber(rhsValue);
        case Operators.LessThanEqualTo:
            return _.toNumber(lhsValue) <= _.toNumber(rhsValue);
        case Operators.GreaterThan:
            return _.toNumber(lhsValue) > _.toNumber(rhsValue);
        case Operators.GreaterThanEqualTo:
            return _.toNumber(lhsValue) >= _.toNumber(rhsValue);
        case Operators.IsBetween:
            return (+lhsValue >= getMinRangeValue(metric) &&
                +lhsValue <= getMaxRangeValues(metric));
        case Operators.Contains:
            return _.includes(_.toString(lhsValue), rhsValue.toString());
        case Operators.DoesNotContain:
            return !_.includes(_.toString(lhsValue), rhsValue.toString());
        case Operators.StartsWith:
            return _.startsWith(lhsValue, rhsValue.toString());
        case Operators.EndsWith:
            return _.endsWith(lhsValue, rhsValue.toString());
        default:
            logger.warn('observed a corrupted client state, there maybe some loss of conditional formatting data');
            return false;
    }
};
export const isConditionSatisfied = (metric, value, parameters, lhsColValue, rhsColValue) => {
    let parameter = null;
    let parameterValue = null;
    switch (metric.comparisonType) {
        case ConditionalFormattingComparisonTypes.ValueBased:
            return doValueBasedComparison(metric, lhsColValue === undefined ? value : lhsColValue, metric.value);
        case ConditionalFormattingComparisonTypes.ParameterBased:
            parameter = parameters === null || parameters === void 0 ? void 0 : parameters.find((parameter) => parameter.id === metric.comparisonParameterId);
            parameterValue =
                (parameter === null || parameter === void 0 ? void 0 : parameter.overrideValue) || (parameter === null || parameter === void 0 ? void 0 : parameter.defaultValue);
            return doValueBasedComparison(metric, lhsColValue === undefined ? value : lhsColValue, parameterValue);
        case ConditionalFormattingComparisonTypes.ColumnBased:
            return doValueBasedComparison(metric, lhsColValue, rhsColValue);
        default:
            return doValueBasedComparison(metric, lhsColValue === undefined ? value : lhsColValue, metric.value);
    }
};
const isV1Rule = (rule, columnId) => {
    const isValueBasedRule = !rule.comparisonType ||
        rule.comparisonType === ConditionalFormattingComparisonTypes.ValueBased;
    const isLhsColumnIdSet = rule.lhsColumnId && rule.lhsColumnId !== columnId;
    return isValueBasedRule && !isLhsColumnIdSet && !rule.isHighlightRow;
};
export const getHighChartMetric = (metric, parametersList = []) => {
    var _a, _b;
    const updatedMetric = {
        color: (_a = metric === null || metric === void 0 ? void 0 : metric.solidBackgroundAttrs) === null || _a === void 0 ? void 0 : _a.color,
        min: -Infinity,
        max: Infinity,
        plotAsBand: metric.plotAsBand,
    };
    let value = metric.value;
    if (metric.comparisonType ===
        ConditionalFormattingComparisonTypes.ParameterBased) {
        const selectedParameter = parametersList === null || parametersList === void 0 ? void 0 : parametersList.find(parameter => parameter.id === metric.comparisonParameterId);
        if (!selectedParameter) {
            return updatedMetric;
        }
        value =
            (_b = selectedParameter.overrideValue) !== null && _b !== void 0 ? _b : selectedParameter.defaultValue;
    }
    switch (metric.operator) {
        case Operators.EqualTo:
            updatedMetric.min = _.toNumber(value);
            updatedMetric.max = _.toNumber(value);
            break;
        case Operators.LessThan:
        case Operators.LessThanEqualTo:
            updatedMetric.max = _.toNumber(value);
            break;
        case Operators.GreaterThan:
        case Operators.GreaterThanEqualTo:
            updatedMetric.min = _.toNumber(value);
            break;
        case Operators.IsBetween:
            updatedMetric.min = metric.rangeValues.min;
            updatedMetric.max = metric.rangeValues.max;
            break;
        default:
    }
    return updatedMetric;
};
export const getUpdatedConditionalMetric = (conditionalFormat) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    if (!conditionalFormat) {
        return null;
    }
    return {
        __typename: 'ConditionalMetric',
        backgroundFormatType: (_a = conditionalFormat.backgroundFormatType) !== null && _a !== void 0 ? _a : null,
        fontProperties: conditionalFormat.fontProperties
            ? {
                __typename: 'FontAttrs',
                bold: (_b = conditionalFormat.fontProperties.bold) !== null && _b !== void 0 ? _b : null,
                color: (_c = conditionalFormat.fontProperties.color) !== null && _c !== void 0 ? _c : null,
                italic: (_d = conditionalFormat.fontProperties.italic) !== null && _d !== void 0 ? _d : null,
                strikeThrough: (_e = conditionalFormat.fontProperties.strikeThrough) !== null && _e !== void 0 ? _e : null,
                underline: (_f = conditionalFormat.fontProperties.underline) !== null && _f !== void 0 ? _f : null,
            }
            : null,
        gradientBackgroundAttrs: conditionalFormat.gradientBackgroundAttrs
            ? {
                __typename: 'GradientBackgroundAttrs',
                colors: conditionalFormat.gradientBackgroundAttrs.colors || null,
                backgroundFormatRange: conditionalFormat.gradientBackgroundAttrs
                    .backgroundFormatRange || null,
                backgroundFormatMidpoint: conditionalFormat.gradientBackgroundAttrs
                    .backgroundFormatMidpoint || null,
                isAutoScaled: (_g = conditionalFormat.gradientBackgroundAttrs.isAutoScaled) !== null && _g !== void 0 ? _g : null,
            }
            : null,
        operator: conditionalFormat.operator || null,
        plotAsBand: (_h = conditionalFormat.plotAsBand) !== null && _h !== void 0 ? _h : null,
        rangeValues: conditionalFormat.rangeValues
            ? {
                __typename: 'Range',
                max: (_j = conditionalFormat.rangeValues.max) !== null && _j !== void 0 ? _j : null,
                min: (_k = conditionalFormat.rangeValues.min) !== null && _k !== void 0 ? _k : null,
            }
            : null,
        value: !_.isNil(conditionalFormat.value)
            ? conditionalFormat.value
            : null,
        solidBackgroundAttrs: conditionalFormat.solidBackgroundAttrs
            ? {
                __typename: 'SolidBackgroundAttrs',
                color: conditionalFormat.solidBackgroundAttrs.color || null,
            }
            : null,
        comparisonType: (_l = conditionalFormat.comparisonType) !== null && _l !== void 0 ? _l : null,
        lhsColumnId: (_m = conditionalFormat.lhsColumnId) !== null && _m !== void 0 ? _m : null,
        rhsColumnId: (_o = conditionalFormat.rhsColumnId) !== null && _o !== void 0 ? _o : null,
        comparisonParameterId: (_p = conditionalFormat.comparisonParameterId) !== null && _p !== void 0 ? _p : null,
        isHighlightRow: (_q = conditionalFormat === null || conditionalFormat === void 0 ? void 0 : conditionalFormat.isHighlightRow) !== null && _q !== void 0 ? _q : null,
    };
};
const getVizColumnById = (vizModel, columnId) => {
    return vizModel.columns.filter(col => getId(col.column) === columnId)[0];
};
export const getConditionalFormats = (vizProp = {}, columnId, typename, vizModel) => {
    var _a, _b, _c, _d;
    let showV2Rules = getEnvFlags().isEnhancedCfEnabled || isEnhancedCfEnabled();
    if (showV2Rules && (vizModel === null || vizModel === void 0 ? void 0 : vizModel.__typename) === 'ChartViz') {
        const options = configOptions(vizModel.config.chartType, vizModel);
        const allowedConfigurations = options.allowedConfigurations;
        const allowEnhancedConditionalFormatting = allowedConfigurations.allowEnhancedConditionalFormatting;
        if (!allowEnhancedConditionalFormatting) {
            showV2Rules = false;
        }
    }
    if (typename === 'HeadlineVizProps') {
        const rules = ((_b = (_a = vizProp.columnProperty) === null || _a === void 0 ? void 0 : _a.conditionalFormatting) === null || _b === void 0 ? void 0 : _b.rows) || [];
        return rules.filter(row => showV2Rules || isV1Rule(row, columnId));
    }
    const columnProperties = getColumnPropertiesById(vizProp, columnId) || {};
    const corruptedRows = _.filter(((_c = columnProperties.conditionalFormatting) === null || _c === void 0 ? void 0 : _c.rows) || [], (row) => _.isNil(row.operator) &&
        row.backgroundFormatType !== BackgroundFormatTypes.Gradient);
    if (corruptedRows.length > 0) {
        logger.debug('corrupted rows found in conditional formatting, parsing the same from backend.');
        let viz;
        if (typename === 'ChartVizProps') {
            viz = vizModel;
        }
        else {
            viz = vizModel;
        }
        const vizColumn = getVizColumnById(viz, columnId);
        const parsedConditionalFormatting = getUpdatedConditionalFormatting({
            id: columnId,
            metricDefinition: vizColumn.legacyMetricDefinition,
        });
        return (parsedConditionalFormatting === null || parsedConditionalFormatting === void 0 ? void 0 : parsedConditionalFormatting.rows) || [];
    }
    const filteredRows = _.filter(((_d = columnProperties.conditionalFormatting) === null || _d === void 0 ? void 0 : _d.rows) || [], row => showV2Rules || isV1Rule(row, columnId));
    return filteredRows.map((row) => {
        var _a, _b;
        const metric = _.cloneDeep(row);
        let rhsColumnName = '';
        const isKPI = getChartType(vizModel) === ChartType.Kpi;
        if (isKPI &&
            metric.comparisonType ===
                ConditionalFormattingComparisonTypes.ColumnBased) {
            rhsColumnName = (_b = (_a = getVizColumnById(vizModel, metric.rhsColumnId)) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.name;
        }
        if (metric.operator === Operators.IsBetween) {
            _.isNull(metric.rangeValues.min)
                ? (metric.rangeValues.min = Number.NEGATIVE_INFINITY)
                : null;
            _.isNull(metric.rangeValues.max)
                ? (metric.rangeValues.max = Number.POSITIVE_INFINITY)
                : null;
        }
        return rhsColumnName ? { ...metric, rhsColumnName } : metric;
    });
};
export const isConditionalFormattingApplied = (vizProps = {}, columns, typename, model) => columns.some(col => getConditionalFormats(vizProps, col.id, typename, model).length > 0);
const getDefaultProperty = (typeName) => {
    if (typeName === 'ChartVizProps') {
        return {
            __typename: 'ChartVizColumnProperties',
            conditionalFormatting: null,
            dataLabels: null,
            excludeNullValues: null,
            handleMissingValues: null,
            customProps: null,
        };
    }
    return {
        __typename: 'TableVizColumnProperties',
        conditionalFormatting: null,
        wrapColumnText: null,
        isHidden: null,
    };
};
export const updateClientStateWithConditionalFormats = (vizProp = {}, typename, columnId, conditionalFormattings) => {
    if (typename === 'HeadlineVizProps') {
        return {
            ...vizProp,
            columnProperty: {
                __typename: 'HeadlineVizProperties',
                summariesNumberFormatConfig: null,
                ...vizProp.columnProperty,
                conditionalFormatting: {
                    __typename: 'ConditionalFormatting',
                    rows: conditionalFormattings.map(conditionalFormat => {
                        return getUpdatedConditionalMetric(conditionalFormat);
                    }),
                },
            },
        };
    }
    const columnPropertyToBeUpdated = getColumnPropertiesById(vizProp, columnId) || getDefaultProperty(typename);
    const filterColumnPropertyMap = _.filter(vizProp.columnProperties, (colMap) => (colMap === null || colMap === void 0 ? void 0 : colMap.columnId) !== columnId) || [];
    const updatedColumnProperty = {
        ...columnPropertyToBeUpdated,
        conditionalFormatting: {
            __typename: 'ConditionalFormatting',
            rows: conditionalFormattings.map(conditionalFormat => {
                return getUpdatedConditionalMetric(conditionalFormat);
            }),
        },
    };
    return {
        ...vizProp,
        columnProperties: [
            ...filterColumnPropertyMap,
            {
                __typename: typename === 'ChartVizProps'
                    ? 'ChartColumnPropertyMap'
                    : 'TableColumnPropertyMap',
                columnId,
                columnProperty: updatedColumnProperty,
            },
        ],
    };
};
export const getColumnsUsedInConditionalFormatting = (chartViz) => {
    const columnIds = [];
    const allRules = getAllConditionalFormattingRules(chartViz);
    chartViz.columns.forEach((column) => {
        const answerColumn = column.column;
        const rulesOnColumn = allRules[answerColumn.id];
        if (rulesOnColumn === null || rulesOnColumn === void 0 ? void 0 : rulesOnColumn.length) {
            rulesOnColumn.forEach((rule) => {
                if (rule.lhsColumnId) {
                    columnIds.push(rule.lhsColumnId);
                }
                if (rule.rhsColumnId) {
                    columnIds.push(rule.rhsColumnId);
                }
                columnIds.push(answerColumn.id);
            });
        }
    });
    return _.uniq(columnIds);
};
export const conditionalFormattingUtilsTestHooks = {
    isV1Rule,
};
const getResetGradientColorsForGeoArea = () => {
    const multiColorSeries = CustomStylingConfig.getGeoAreaColors()[0];
    return multiColorSeries
        ? [
            multiColorSeries[0],
            multiColorSeries[Math.floor(multiColorSeries.length / 2)],
            multiColorSeries[multiColorSeries.length - 1],
        ]
        : null;
};
const createGradientColorsFromHeatmapConstants = () => {
    return HEATMAP_GRADIENT_COLORS
        ? [
            HEATMAP_GRADIENT_COLORS[0],
            HEATMAP_GRADIENT_COLORS[Math.floor(HEATMAP_GRADIENT_COLORS.length / 2)],
            HEATMAP_GRADIENT_COLORS[HEATMAP_GRADIENT_COLORS.length - 1],
        ]
        : null;
};
export const getDefaultGradientColors = (viz, data) => {
    const chartViz = viz;
    const dataModel = data;
    if (!isGradientColoringEnabled()) {
        return null;
    }
    if (isHighchartType(getChartType(chartViz))) {
        const colorScale = getColorScaleForSerie(chartViz, dataModel);
        return [colorScale[0], '', colorScale[1]];
    }
    if (isGeoMapChartType(getChartType(chartViz))) {
        if (getChartType(chartViz) === ChartType.GeoArea) {
            return getResetGradientColorsForGeoArea();
        }
        if (getChartType(chartViz) === ChartType.GeoHeatmap) {
            return createGradientColorsFromHeatmapConstants();
        }
    }
    return null;
};
//# sourceMappingURL=conditional-formatting-utils.js.map